import { Chip, IconButton, Tooltip } from '@mui/material';
import type { OrgMember } from '../../types';
import { USERS_TAB_LABELS as LABELS, ROLE_LABELS } from '../../../../utils/labels';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import EditIcon from '@mui/icons-material/Edit';

interface MemberRowProps {
  member: OrgMember;
  isSelf?: boolean;
  showDivider: boolean;
  showRole?: boolean;
  onDeactivate?: () => void;
  onReactivate?: () => void;
  onEditRole?: () => void;
}

export default function MemberRow({
  member: m,
  isSelf = false,
  showDivider,
  showRole = false,
  onDeactivate,
  onReactivate,
  onEditRole,
}: MemberRowProps) {
  const isActive = m.active !== false;
  const hasName = !!(m.first_name || m.last_name);
  const fullName = [m.first_name, m.last_name].filter(Boolean).join(' ');

  return (
    <div>
      {showDivider && <hr className="border-border-subtle" />}
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex-1 min-w-0">
          {hasName && (
            <div className="flex items-center gap-1.5 min-w-0">
              <p
                className={`font-medium truncate mb-1 mt-0 ${!isActive ? 'text-text-secondary' : ''}`}
              >
                {fullName}
              </p>
              {isSelf && (
                <Chip
                  label={LABELS.you}
                  className="ml-1"
                  size="small"
                />
              )}
              {showRole && <Chip label={ROLE_LABELS[m.role]} className="ml-1" size="small" />}
            </div>
          )}
          <p
            className={`text-sm mb-0 mt-0 truncate ${hasName ? 'text-text-secondary' : 'font-medium'} ${!isActive ? 'text-text-secondary' : ''}`}
          >
            {m.email ?? LABELS.noEmail}
          </p>
          {isActive && m.joined_at && (
            <time dateTime={m.joined_at} className="text-xs text-text-secondary">
              {LABELS.memberSince}{' '}
              {new Date(m.joined_at).toLocaleDateString('nl-BE', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </time>
          )}
        </div>
        {isActive ? (
          <>
            {!isSelf && onEditRole && (
              <Tooltip title={LABELS.editRole}>
                <IconButton onClick={onEditRole} aria-label={LABELS.editRole} color="primary">
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isSelf && onDeactivate && (
              <Tooltip title={LABELS.deactivate}>
                <IconButton onClick={onDeactivate} aria-label={LABELS.deactivate} color="warning">
                  <ArchiveOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        ) : (
          onReactivate && (
            <Tooltip title={LABELS.reactivate}>
              <IconButton onClick={onReactivate} aria-label={LABELS.reactivate} color="success">
                <UnarchiveOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )
        )}
      </div>
    </div>
  );
}
